import React, { useEffect } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { useAppSelector } from './store/hooks';
import routes from './utils/routes';
import './App.css';

// Layout
import MainLayout from './components/layout/MainLayout';

// Pages
import Landing from './pages/Landing';
import Onboarding from './pages/Onboarding';
import Connect from './pages/auth/Connect';
import Auth from './pages/auth/Auth';
import CreatePassword from './pages/auth/CreatePassword';
import OTPVerification from './pages/auth/OTPVerification';
import UserDetails from './pages/auth/UserDetails';
import { Home, Notification, WatchList } from './pages/home';
import Market from './pages/market/Market';
import { Buckets, BucketDetail } from './pages/buckets';
import { CompanyDetail } from './pages/company';
import { SectorDashboard } from './pages/sector';
import Portfolio from './pages/portfolio/Portfolio';
import {
  Profile,
  PersonalDetails,
  SelectLanguage,
  HelpCenter,
  FAQScreen,
  PrivacyPolicies,
  TermsAndCondition,
  AboutApp,
  SupportTicket,
} from './pages/profile';
import Wallet from './pages/wallet/Wallet';
import {
  ExpertSearch,
  ExpertProfile,
  ExpertBooking,
  BookingConfirmation,
  ChatScreen,
  CallScreen,
  RateExpert,
  ExpertSettings,
  AvailabilityManagement,
  SessionRates,
  SebiCredentials,
  WalletAndPayouts,
  ExpertSpecializations,
  ExpertCallsManagement,
  NewStockCall,
} from './pages/expert';
import { Copilot } from './pages/copilot';
import { Pricing } from './pages/pricing';
import AdminLayout from './components/layout/AdminLayout';
import {
  AdminDashboard, AdminUsers, AdminUserDetail, AdminPayments,
  AdminBookings, AdminExperts, AdminEmail, AdminBlacklist,
  AdminSupport, AdminAuditLog, AdminBuckets, AdminBucketDetail,
  AdminCompanyInsights, AdminLLMConfig, AdminEmbeddingConfig,
} from './pages/admin';

function App() {
  const colors = useAppSelector((state) => state.theme.theme);
  const isDark = useAppSelector((state) => state.theme.isDark);

  useEffect(() => {
    // Apply theme class to document
    if (isDark) {
      document.documentElement.classList.add('dark');
    } else {
      document.documentElement.classList.remove('dark');
    }
  }, [isDark]);

  return (
    <div className="min-h-screen" style={{ backgroundColor: colors.backgroundColor, color: colors.textColor }}>
      <Routes>
        {/* Landing page (replaces splash) */}
        <Route path={routes.landing} element={<Landing />} />

        {/* Public routes */}
        <Route path={routes.onboarding} element={<Onboarding />} />
        <Route path={routes.connect} element={<Connect />} />
        <Route path={routes.login} element={<Auth />} />
        <Route path={routes.signUp} element={<Auth />} />
        <Route path={routes.createPassword} element={<CreatePassword />} />
        <Route path={routes.otpVerification} element={<OTPVerification />} />
        <Route path={routes.userDetails} element={<UserDetails />} />

        {/* Main app routes with bottom navigation */}
        <Route element={<MainLayout />}>
          <Route path={routes.home} element={<Home />} />
          <Route path={routes.market} element={<Market />} />
          <Route path={routes.buckets} element={<Buckets />} />
          <Route path={routes.bucketDetail} element={<BucketDetail />} />
          <Route path={routes.portfolio} element={<Portfolio />} />
          <Route path={routes.profile} element={<Profile />} />
          <Route path={routes.wallet} element={<Wallet />} />
          <Route path={routes.copilot} element={<Copilot />} />
          <Route path={routes.companyDetail} element={<CompanyDetail />} />
          <Route path={routes.sectorDetail} element={<SectorDashboard />} />
          <Route path={routes.marketMonitor} element={<Navigate to="/market" replace />} />
          <Route path={routes.expertSearch} element={<ExpertSearch />} />
          <Route path={routes.expertProfile} element={<ExpertProfile />} />
          <Route path={routes.expertBooking} element={<ExpertBooking />} />
          <Route path={routes.expertBookingConfirmation} element={<BookingConfirmation />} />
          <Route path={routes.chatScreen} element={<ChatScreen />} />
          <Route path={routes.callScreen} element={<CallScreen />} />
          <Route path={routes.rateExpert} element={<RateExpert />} />
          <Route path={routes.allBookings} element={<Navigate to={routes.expertSearch} replace />} />
          {/* Expert Settings */}
          <Route path={routes.expertSettings} element={<ExpertSettings />} />
          <Route path={routes.expertAvailability} element={<AvailabilityManagement />} />
          <Route path={routes.expertRates} element={<SessionRates />} />
          <Route path={routes.expertSebiCredentials} element={<SebiCredentials />} />
          <Route path={routes.expertWallet} element={<WalletAndPayouts />} />
          <Route path={routes.expertSpecializations} element={<ExpertSpecializations />} />
          <Route path={routes.expertCalls} element={<ExpertCallsManagement />} />
          <Route path={routes.expertNewCall} element={<NewStockCall />} />
          <Route path={routes.notification} element={<Notification />} />
          <Route path={routes.watchListHomeCard} element={<WatchList />} />
          {/* Profile sub-pages */}
          <Route path={routes.personalDetails} element={<PersonalDetails />} />
          <Route path={routes.pushNotification} element={<Navigate to={routes.notification} replace />} />
          <Route path={routes.selectLanguage} element={<SelectLanguage />} />
          <Route path={routes.helpCenter} element={<HelpCenter />} />
          <Route path={routes.faqScreen} element={<FAQScreen />} />
          <Route path={routes.privacyPolicies} element={<PrivacyPolicies />} />
          <Route path={routes.termsAndCondition} element={<TermsAndCondition />} />
          <Route path={routes.aboutApp} element={<AboutApp />} />
          <Route path={routes.supportTicket} element={<SupportTicket />} />
          {/* Subscription */}
          <Route path={routes.pricing} element={<Pricing />} />
        </Route>

        {/* Admin routes */}
        <Route element={<AdminLayout />}>
          <Route path={routes.adminDashboard} element={<AdminDashboard />} />
          <Route path={routes.adminUsers} element={<AdminUsers />} />
          <Route path={routes.adminUserDetail} element={<AdminUserDetail />} />
          <Route path={routes.adminPayments} element={<AdminPayments />} />
          <Route path={routes.adminBookings} element={<AdminBookings />} />
          <Route path={routes.adminExperts} element={<AdminExperts />} />
          <Route path={routes.adminEmail} element={<AdminEmail />} />
          <Route path={routes.adminBlacklist} element={<AdminBlacklist />} />
          <Route path={routes.adminSupport} element={<AdminSupport />} />
          <Route path={routes.adminAuditLog} element={<AdminAuditLog />} />
          <Route path={routes.adminBuckets} element={<AdminBuckets />} />
          <Route path={routes.adminBucketDetail} element={<AdminBucketDetail />} />
          <Route path={routes.adminCompanyInsights} element={<AdminCompanyInsights />} />
          <Route path={routes.adminLLMConfig} element={<AdminLLMConfig />} />
          <Route path={routes.adminEmbeddingConfig} element={<AdminEmbeddingConfig />} />
        </Route>

        {/* Fallback route */}
        <Route path="*" element={<Navigate to={routes.landing} replace />} />
      </Routes>
    </div>
  );
}

export default App;
